/**
 * Quest Chain Manager
 * Manages quest chains (organizational groupings of quests)
 * Provides CRUD operations and localStorage persistence
 */

import { QuestChain, GameEvent } from '@/types';
import { eventBus } from '@/services/EventBus';

const STORAGE_KEY = 'custom_quest_chains';

class QuestChainManager {
  private chains: Map<string, QuestChain> = new Map();

  constructor() {
    this.load();
  }

  /**
   * Load quest chains from localStorage
   */
  load(): void {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      console.log('[QuestChainManager] No chains found, starting fresh');
      return;
    }

    try {
      const chains: QuestChain[] = JSON.parse(stored);
      this.chains.clear();
      chains.forEach(chain => this.chains.set(chain.id, chain));
      console.log(`[QuestChainManager] Loaded ${chains.length} chains`);
    } catch (error) {
      console.error('[QuestChainManager] Failed to load chains:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Save quest chains to localStorage
   */
  save(): void {
    const chains = Array.from(this.chains.values());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chains, null, 2));
    eventBus.emit(GameEvent.QUEST_CHAIN_UPDATED, {});
  }

  /**
   * Create a new quest chain
   */
  createChain(name: string, options?: Partial<QuestChain>): QuestChain {
    const chain: QuestChain = {
      id: `chain_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      displayOrder: this.chains.size,
      collapsed: false,
      ...options,
    };

    this.chains.set(chain.id, chain);
    this.save();

    console.log(`[QuestChainManager] Created chain: ${chain.name} (${chain.id})`);
    return chain;
  }

  /**
   * Update an existing quest chain
   */
  updateChain(chainId: string, updates: Partial<QuestChain>): void {
    const chain = this.chains.get(chainId);
    if (!chain) {
      console.warn(`[QuestChainManager] Chain not found: ${chainId}`);
      return;
    }

    Object.assign(chain, updates);
    this.save();

    console.log(`[QuestChainManager] Updated chain: ${chain.name} (${chainId})`);
  }

  /**
   * Delete a quest chain
   * NOTE: Quests in the chain should be reassigned before calling this
   */
  deleteChain(chainId: string): void {
    if (!this.chains.has(chainId)) {
      console.warn(`[QuestChainManager] Cannot delete non-existent chain: ${chainId}`);
      return;
    }

    const chain = this.chains.get(chainId)!;
    this.chains.delete(chainId);
    this.save();

    console.log(`[QuestChainManager] Deleted chain: ${chain.name} (${chainId})`);
  }

  /**
   * Get a single quest chain by ID
   */
  getChain(chainId: string): QuestChain | undefined {
    return this.chains.get(chainId);
  }

  /**
   * Get all quest chains, sorted by displayOrder
   */
  getAllChains(): QuestChain[] {
    return Array.from(this.chains.values())
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Reorder quest chains
   * Updates displayOrder based on array position
   */
  reorderChains(chainIds: string[]): void {
    chainIds.forEach((id, index) => {
      const chain = this.chains.get(id);
      if (chain) {
        chain.displayOrder = index;
      }
    });

    this.save();
    console.log('[QuestChainManager] Reordered chains');
  }

  /**
   * Toggle chain collapsed state
   */
  toggleChainCollapsed(chainId: string): void {
    const chain = this.chains.get(chainId);
    if (chain) {
      chain.collapsed = !chain.collapsed;
      this.save();
    }
  }

  /**
   * Register default/baked-in chains
   * Only adds chains that don't already exist (won't overwrite user edits in localStorage)
   */
  registerDefaultChains(defaults: QuestChain[]): void {
    let added = 0;
    for (const chain of defaults) {
      if (!this.chains.has(chain.id)) {
        this.chains.set(chain.id, chain);
        added++;
      }
    }
    if (added > 0) {
      this.save();
      console.log(`[QuestChainManager] Registered ${added} default chains`);
    }
  }

  /**
   * Check if a chain exists
   */
  chainExists(chainId: string): boolean {
    return this.chains.has(chainId);
  }

  /**
   * Clear all chains (for testing/reset)
   */
  clearAll(): void {
    this.chains.clear();
    localStorage.removeItem(STORAGE_KEY);
    console.log('[QuestChainManager] Cleared all chains');
  }
}

// Export singleton instance
export const questChainManager = new QuestChainManager();
